/**
 * VoiceRecorderModal — record a short voice note via MediaRecorder and
 * upload it to the backend. Used by the coach to leave a comment on a
 * player's shot attempt.
 *
 * - Hard cap of 60s (`MAX_MS`) keeps notes snappy and storage cheap.
 * - We pause-on-tab-hidden and stop-on-close so we never leak the mic.
 * - Audio is sent as multipart/form-data with the browser's native MIME
 *   (Chromium: audio/webm; Safari: audio/mp4). Backend whitelist covers
 *   both.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mic, Square, Play, Pause, X, Send, Loader2, Trash2 } from 'lucide-react';
import { useLang } from '@/context/LangContext';
import { api } from '@/lib/api';

const MAX_MS = 60_000;

export default function VoiceRecorderModal({ attemptId, playerName, onClose, onSent }) {
  const { t } = useLang();
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  // Cleanup on unmount.
  useEffect(() => () => stopAll(), []);

  const stopAll = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    streamRef.current?.getTracks().forEach((tk) => tk.stop());
    streamRef.current = null;
  };

  const beginRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Choose the most compatible MIME the browser actually supports.
      const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
      const mimeType = candidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || '';
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const finalType = chunksRef.current[0]?.type || mimeType || 'audio/webm';
        const b = new Blob(chunksRef.current, { type: finalType });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        streamRef.current?.getTracks().forEach((tk) => tk.stop());
        streamRef.current = null;
      };
      rec.start(250);
      startedAtRef.current = performance.now();
      setRecording(true);
      setPaused(false);
      setBlob(null);
      setBlobUrl((u) => { if (u) URL.revokeObjectURL(u); return null; });
      tickRef.current = setInterval(() => {
        const ms = performance.now() - startedAtRef.current;
        setElapsed(ms);
        if (ms >= MAX_MS) stopRecording();
      }, 100);
    } catch (e) {
      setError(t('sa_voice_mic_denied'));
    }
  };

  const stopRecording = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    try { recorderRef.current?.stop(); } catch { /* noop */ }
    setRecording(false);
    setPaused(false);
  };

  const togglePause = () => {
    const rec = recorderRef.current;
    if (!rec) return;
    if (rec.state === 'recording') { rec.pause(); setPaused(true); }
    else if (rec.state === 'paused') { rec.resume(); setPaused(false); }
  };

  const discard = () => {
    setBlob(null);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setElapsed(0);
  };

  const send = async () => {
    if (!blob || !attemptId) return;
    setSending(true);
    try {
      // The blob's MIME may include codecs (`audio/webm;codecs=opus`).
      // Strip the parameters so the multipart field uses a clean
      // content-type that matches our backend whitelist.
      const cleanType = (blob.type || 'audio/webm').split(';')[0];
      const ext = cleanType.split('/')[1] || 'webm';
      const form = new FormData();
      form.append('audio', new File([blob], `voice-${attemptId}.${ext}`, { type: cleanType }));
      const { data } = await api.post(`/shot-analyzer/voice-comment/${attemptId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('sa_voice_sent'));
      onSent?.(data?.comment);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('sa_voice_send_fail'));
    } finally {
      setSending(false);
    }
  };

  const seconds = Math.floor(elapsed / 1000);
  const remaining = Math.max(0, MAX_MS / 1000 - seconds);

  return (
    <div
      data-testid="sa-voice-recorder-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={() => !sending && onClose?.()}
    >
      <div
        className="w-full max-w-md rounded-xl border-2 border-gold/40 bg-navy-deep p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold/80">{t('sa_voice_modal_eyebrow')}</div>
            <h3 className="text-lg font-h text-white mt-1">{t('sa_voice_modal_title')}</h3>
            {playerName && (
              <div className="text-xs text-slate-400 mt-0.5">{t('sa_voice_for')}: <span className="text-white">{playerName}</span></div>
            )}
          </div>
          <button
            data-testid="sa-voice-close"
            onClick={onClose}
            disabled={sending}
            className="p-2 rounded-md hover:bg-white/5 text-slate-300 disabled:opacity-40"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Recorder waveform / timer */}
        <div className="mt-5 rounded-lg border border-white/10 bg-black/40 p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div
              className={`w-3 h-3 rounded-full ${recording && !paused ? 'bg-red-500 animate-pulse' : recording && paused ? 'bg-amber-400' : 'bg-slate-600'}`}
            />
            <div className="text-3xl font-mono tabular-nums text-white">
              {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
            {recording ? t('sa_voice_remaining').replace('{n}', String(Math.ceil(remaining))) : t('sa_voice_max').replace('{n}', '60')}
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-200">{error}</div>
        )}

        {/* Controls */}
        <div className="mt-5 flex items-center justify-center gap-3">
          {!recording && !blob && (
            <button
              data-testid="sa-voice-start"
              onClick={beginRecording}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-red-700 hover:bg-red-600 text-white text-sm font-bold uppercase tracking-widest"
            >
              <Mic className="w-4 h-4" /> {t('sa_voice_record')}
            </button>
          )}
          {recording && (
            <>
              <button
                onClick={togglePause}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-xs font-mono uppercase tracking-widest"
              >
                {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                {paused ? t('sa_voice_resume') : t('sa_voice_pause')}
              </button>
              <button
                data-testid="sa-voice-stop"
                onClick={stopRecording}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-red-700 hover:bg-red-600 text-white text-xs font-mono uppercase tracking-widest"
              >
                <Square className="w-3.5 h-3.5" /> {t('sa_voice_stop')}
              </button>
            </>
          )}
          {blob && !recording && (
            <button
              onClick={discard}
              className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-xs font-mono uppercase tracking-widest"
            >
              <Trash2 className="w-3.5 h-3.5" /> {t('sa_voice_discard')}
            </button>
          )}
        </div>

        {/* Preview + send */}
        {blob && blobUrl && (
          <div className="mt-5 space-y-3">
            <audio data-testid="sa-voice-preview" src={blobUrl} controls className="w-full" />
            <button
              data-testid="sa-voice-send"
              onClick={send}
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-gold text-navy text-sm font-bold uppercase tracking-widest hover:brightness-110 disabled:opacity-60"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? t('sa_voice_sending') : t('sa_voice_send')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
